import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@dreamtree-org/twreact-ui';
import { AlertOctagon, AlertTriangle, ArrowRight, Boxes, Clock, IndianRupee, PackageCheck, Plus, ShieldCheck, Truck } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageHeader from '@/components/PageHeader';
import StatTile from '@/components/StatTile';
import ExpiryTimeline from '@/components/ExpiryTimeline';
import ExpiryBadge from '@/components/ExpiryBadge';
import ProductImage from '@/components/ProductImage';
import ExpiryEditor from '@/components/ExpiryEditor';
import RestockDialog from '@/components/RestockDialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { currencyShort, date, errorMessage, number, quantity } from '@/lib/format';
import { expiryMeta } from '@/lib/expiry';
import Panel from '@/components/Panel';

/**
 * The answer to "what do I deal with today", above the fold.
 *
 * Reading order is deliberate: the count of what has already gone bad, then
 * what is about to, then the money those two represent — and only then the
 * shape of the next ninety days. Every tile is a link into the product list
 * pre-filtered to exactly what it counted, so a number is never a dead end.
 */
export default function Dashboard() {
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [restocking, setRestocking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetchApi('productStats');
      setStats((res && res.data) || null);
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not load the warehouse summary'));
    } finally {
      setLoading(false);
    }
    // fetchApi identity changes each render in the library hook — run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const goto = (state) => nav(`/products?state=${state}`);

  if (loading) return <LoadingState rows={8} />;
  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!stats) return <ErrorState message="No warehouse has been set up yet." onRetry={load} />;

  const c = stats.counts || {};
  const actionable = (c.expired || 0) + (c.critical || 0) + (c.warning || 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={stats.warehouse ? stats.warehouse.name : 'Warehouse'}
        subtitle={`${number(stats.total_products)} product batches tracked · critical within ${stats.policy.criticalDays} days, warning within ${stats.policy.warningDays}`}
        actions={
          <Can module="inventory" action="create">
            <Button leftIcon={<Plus size={16} />} onClick={() => nav('/products/new')}>
              Add product
            </Button>
          </Can>
        }
      />

      {/* The headline row: what is wrong, what is about to be, and what it costs. */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Expired"
          value={number(c.expired)}
          hint="Past date — pull from the shelf"
          meta={expiryMeta('expired')}
          icon={<AlertOctagon size={22} />}
          urgent={c.expired > 0}
          onClick={() => goto('expired')}
        />
        <StatTile
          label={`Critical · next ${stats.policy.criticalDays} days`}
          value={number(c.critical)}
          hint="Move these today"
          meta={expiryMeta('critical')}
          icon={<AlertTriangle size={22} />}
          urgent={c.critical > 0}
          onClick={() => goto('critical')}
        />
        <StatTile
          label={`Expiring soon · ${stats.policy.warningDays} days`}
          value={number(c.warning)}
          hint="Plan a markdown or a transfer"
          meta={expiryMeta('warning')}
          icon={<Clock size={22} />}
          onClick={() => goto('warning')}
        />
        <StatTile
          label="Value at risk"
          value={currencyShort(stats.value_at_risk)}
          hint={`Across ${number(actionable)} batches needing action`}
          icon={<IndianRupee size={22} />}
          onClick={() => goto('actionable')}
        />
      </div>

      {/* The calmer row: everything that is fine, and everything that isn't tracked. */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="On the horizon"
          value={number(c.watch)}
          hint="Due within the quarter"
          meta={expiryMeta('watch')}
          onClick={() => goto('watch')}
        />
        <StatTile
          label="Fresh"
          value={number(c.fresh)}
          hint="Well within shelf life"
          meta={expiryMeta('fresh')}
          icon={<ShieldCheck size={22} />}
          onClick={() => goto('fresh')}
        />
        <StatTile
          label="No expiry date"
          value={number(c.unknown)}
          hint="Add a date so these can be tracked"
          meta={expiryMeta('unknown')}
          onClick={() => goto('unknown')}
        />
        {stats.awaiting_delivery > 0 ? (
          <StatTile
            label="Awaiting delivery"
            value={number(stats.awaiting_delivery)}
            hint="Stocked out — record the date when it arrives"
            icon={<Truck size={22} />}
            onClick={() => goto('awaiting')}
          />
        ) : (
          <StatTile
            label="Total stock"
            value={number(Math.round(stats.total_quantity))}
            hint={`${number(stats.total_products)} batches in ${stats.by_category.length} categories`}
            icon={<Boxes size={22} />}
            onClick={() => nav('/products')}
          />
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-5">
        {/* When the shelf empties */}
        <Panel className="flex flex-col p-4 sm:p-5 xl:col-span-3">
          <div className="mb-4">
            <h2 className="text-base font-bold text-[var(--app-text)]">When stock expires</h2>
            <p className="text-sm text-[var(--app-muted)]">
              Batches falling due in each window. Click a bar to see what's in it.
            </p>
          </div>
          <div className="flex-1">
            <ExpiryTimeline
              timeline={stats.timeline || []}
              onSelect={(bucket) => {
                const map = { expired: 'expired', d0_7: 'critical', d8_15: 'warning', d16_30: 'warning', d31_60: 'watch', d61_90: 'watch', beyond: 'fresh' };
                goto(map[bucket.key] || '');
              }}
            />
          </div>
        </Panel>

        {/* Per-category pressure */}
        <Panel className="p-4 sm:p-5 xl:col-span-2">
          <div className="mb-4">
            <h2 className="text-base font-bold text-[var(--app-text)]">Pressure by category</h2>
            <p className="text-sm text-[var(--app-muted)]">
              How much of each category needs attention.
            </p>
          </div>
          <div className="space-y-3">
            {stats.by_category.slice(0, 8).map((row) => {
              const bad = row.expired + row.critical + row.warning;
              const pct = row.total ? Math.round((bad / row.total) * 100) : 0;
              return (
                <button
                  key={row.category}
                  onClick={() => nav(`/products?category=${encodeURIComponent(row.category)}`)}
                  className="w-full text-left"
                >
                  <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                    <span className="truncate font-medium text-[var(--app-text)]">{row.category}</span>
                    <span className="nums shrink-0 text-xs text-[var(--app-muted)]">
                      {bad} of {row.total}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-[var(--app-border)]">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        background:
                          pct >= 50 ? 'var(--exp-expired-solid)' : pct >= 20 ? 'var(--exp-warning-solid)' : 'var(--exp-fresh-solid)',
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* The worklist */}
      <Panel className="p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-[var(--app-text)]">Needs attention first</h2>
            <p className="text-sm text-[var(--app-muted)]">
              The soonest to expire, worst first. Tap a date to correct it.
            </p>
          </div>
          <Button variant="outline" size="sm" rightIcon={<ArrowRight size={15} />} onClick={() => goto('actionable')}>
            See all {number(actionable)}
          </Button>
        </div>

        {stats.attention.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck size={40} />}
            title="Nothing needs attention"
            message="No batch in this warehouse is expired or inside the warning window."
          />
        ) : (
          <div className="grid gap-2.5 md:grid-cols-2">
            {stats.attention.map((p, i) => {
              const meta = expiryMeta(p.expiry_state);
              return (
                <div
                  key={p.id}
                  className="zz-card-in flex items-center gap-3 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-2.5"
                  style={{ borderLeft: `4px solid ${meta.solid}`, animationDelay: `${i * 35}ms` }}
                >
                  <button onClick={() => nav(`/products/${p.id}`)} className="shrink-0">
                    <ProductImage src={p.image_url} name={p.name} className="h-12 w-12" />
                  </button>
                  <button onClick={() => nav(`/products/${p.id}`)} className="min-w-0 flex-1 text-left">
                    <div className="truncate text-sm font-semibold text-[var(--app-text)]">{p.name}</div>
                    <div className="truncate text-xs text-[var(--app-muted)]">
                      {p.sku} · {quantity(p.quantity, p.unit)} · {date(p.expiry_date)}
                    </div>
                  </button>
                  <Can module="inventory" action="update" fallback={<ExpiryBadge state={p.expiry_state} days={p.days_to_expiry} variant="chip" />}>
                    <div className="flex shrink-0 items-center gap-1">
                      <button onClick={() => setEditing(p)} title="Update expiry date">
                        <ExpiryBadge state={p.expiry_state} days={p.days_to_expiry} variant="chip" />
                      </button>
                      <button
                        onClick={() => setRestocking(p)}
                        title="Stock out & reorder"
                        aria-label={`Stock out and reorder ${p.name}`}
                        className="rounded-md p-1.5 text-[var(--app-muted)] transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        <PackageCheck size={16} />
                      </button>
                    </div>
                  </Can>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <RestockDialog
        product={restocking}
        policy={stats.policy}
        isOpen={!!restocking}
        onClose={() => setRestocking(null)}
        onSaved={load}
      />

      <ExpiryEditor
        product={editing}
        policy={stats.policy}
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}
